import React, { useState } from "react"
import {
    Container,
    NoElectionsSubtitle,
    NoElectionsTitle,
    NoElectionsWrapper,
} from "QuorumGrassroots/campaign-forms/components/GotvV2Form/GotvV2Form.styles"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import { shouldShowUserInfoUpdateBlock } from "QuorumGrassroots/campaign-forms/helpers"
import UserInfoUpdateBlock from "QuorumGrassroots/campaign-forms/components/ReusableForms/UserInfoUpdateBlock"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CountdownTimer } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CountdownTimer/CountdownTimer"
import { useElectionCountdown } from "./hooks/useElectionCountdown"
import { LandingPage } from "./components/LandingPage/LandingPage"
import { parseElectionDatetime } from "./GotvV2Form.helper"
import { Campaign } from "@/types/djangio"

interface GotvV2FormProps {
    campaign: Campaign
    t: (key: string) => string
    uniqueWidgetId: string
    isSubmitting?: boolean
    postGrassrootsSupporterAction: (payload: any) => void
    styledButtonText: string
}

export const GotvV2Form: React.FC<GotvV2FormProps> = ({
    campaign,
    t,
    uniqueWidgetId,
    isSubmitting,
    postGrassrootsSupporterAction,
    styledButtonText,
}) => {
    const [showSupporterUpdate, setShowSupporterUpdate] = useState(false)

    const {
        data: electionCountdown,
        isLoading: isElectionCountdownLoading,
        isError: isElectionCountdownError,
    } = useElectionCountdown(String(campaign.id))

    const targetDate = parseElectionDatetime(electionCountdown?.election_datetime)

    const hasValidElectionDate = Boolean(targetDate)
    const elections = (electionCountdown?.elections ?? []) as unknown[]
    const hasElections = elections.length > 0
    const shouldShowCountdown = hasValidElectionDate && !isElectionCountdownError
    const shouldShowNoElectionsState = !isElectionCountdownLoading && !isElectionCountdownError && !hasElections

    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign.social_share_text || `Help ${campaign.name} get out the vote!`,
        imageUrl: campaign.social_image_url,
    })

    return (
        <Container className="gotv-v2-campaign-main">
            <CampaignTitleSubtitle campaign={campaign} />

            {shouldShowUserInfoUpdateBlock() && (
                <UserInfoUpdateBlock
                    campaign={campaign}
                    uniqueWidgetId={uniqueWidgetId}
                    onSupporterFormToggle={setShowSupporterUpdate}
                />
            )}
            {!showSupporterUpdate && (
                <>
                    {shouldShowCountdown ? (
                        <CountdownTimer
                            targetDate={targetDate as Date}
                            overrideTitle={campaign.configs?.countdown_heading}
                        />
                    ) : !isElectionCountdownLoading ? (
                        isElectionCountdownError ? (
                            <p className="gotv-v2-countdown-unavailable">
                                Election countdown is temporarily unavailable.
                            </p>
                        ) : shouldShowNoElectionsState ? (
                            <NoElectionsWrapper data-cy="gotv-no-elections-state">
                                <NoElectionsTitle>No elections are scheduled in your area right now.</NoElectionsTitle>
                                <NoElectionsSubtitle>Check your registration status to be ready.</NoElectionsSubtitle>
                            </NoElectionsWrapper>
                        ) : null
                    ) : null}
                    <LandingPage campaign={campaign} hasUpcomingElection={!shouldShowNoElectionsState} />
                    <GotvCampaignFooter
                        socialPlatforms={socialPlatforms}
                        socialHeading={campaign.social_header || DEFAULT_SOCIAL_HEADER}
                    />
                </>
            )}
        </Container>
    )
}
